import type {RUMEventName} from '@packages/shared/segment-typewriter';
import {now} from './now';
import {telemetry} from './telemetry';

/**
 * Used to measure the latency of a given event. Usage:
 *
 * const profiler = new LatencyProfiler('my-event');
 *
 * // Start the profiler when an event starts (for example, when hovering over
 * // an element).
 * profiler.start(event);
 *
 * // Finish the profiler when the event ends (for example, when an outline has been
 * // rendered on that element).
 * profiler.finish();
 *
 * // Cancel an ongoing profiling
 * profiler.cancel();
 *
 * You can extend this class to add more functionality, see `TextInputLatencyProfiler`
 * as an example.
 */
export class LatencyProfiler {
  private timeoutId: NodeJS.Timeout | null = null;

  // We are using a queue to prevent over-reporting the latency. We will report
  // the average latency of the last 500ms.
  private startTimestamp: number | null = null;
  private queue: number[] = [];
  private eventName: string;
  private debounceTiming = 500;
  private tags: Record<string, string> = {};

  private metricsReportingLabel: RUMEventName;

  constructor(eventName: string) {
    this.eventName = eventName;

    this.metricsReportingLabel = `datadog::${this.eventName}`;
  }

  setTags(tags: Record<string, string>): void {
    this.tags = tags;
  }

  // Used to start profiling latency from a given event.
  start(): void {
    if (!this.startTimestamp) {
      this.startTimestamp = now();
    }
  }

  private report(value: number) {
    telemetry.sendEvent(this.metricsReportingLabel, {
      value,
      ...this.tags,
    });
  }

  private parseQueue(): number {
    if (this.queue.length === 0) {
      return 0;
    }

    // Calculate average of all the marks in the queue.
    return (
      this.queue.reduce<number>((acc, mark) => {
        return acc + mark;
      }, 0) / this.queue.length
    );
  }

  private flushQueue() {
    this.queue = [];
  }

  finish() {
    // If we haven't started measuring, we don't want to report anything.
    if (!this.startTimestamp) {
      return;
    }

    const latency = now() - this.startTimestamp;

    // We are queueing this latency instead of reporting them straight away to prevent
    // over-reporting the latency. We will report the average latency of the last 500ms.
    // Results won't be as exact but we are aiming for a general idea of the latency.
    // We could sum up latency and then divide by the number of latencies, but a queue
    // is more extensible and allows us to filter results if needed.
    this.queue.push(latency);

    this.startTimestamp = null;

    // Sort of a debounce here.
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      const averageLatency = this.parseQueue();
      this.report(averageLatency);
      this.flushQueue();
    }, this.debounceTiming);
  }

  // cancel an ongoing profiling.
  cancel() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.flushQueue();
    this.startTimestamp = null;
  }
}
